/**
 * @module SalesChat
 * @description Öffentliche Chat-Seite für AI Sales Agent.
 * Token-basiert, kein Login nötig.
 */
import { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';

const API = import.meta.env.VITE_API_URL || '';

function TypingIndicator() {
  return (
    <div className="flex items-start gap-3 max-w-[80%]">
      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center flex-shrink-0">
        <span className="text-white font-bold text-xs">W</span>
      </div>
      <div className="bg-gray-100 rounded-2xl rounded-tl-sm px-4 py-3">
        <div className="flex gap-1.5">
          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
          <div className="w-2 h-2 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
        </div>
      </div>
    </div>
  );
}

function ChatBubble({ rolle, inhalt }) {
  const isUser = rolle === 'user';
  return (
    <div className={`flex items-start gap-3 ${isUser ? 'flex-row-reverse' : ''} max-w-[85%] ${isUser ? 'ml-auto' : ''}`}>
      {!isUser && (
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold text-xs">W</span>
        </div>
      )}
      <div className={`rounded-2xl px-4 py-3 ${
        isUser
          ? 'bg-primary-600 text-white rounded-tr-sm'
          : 'bg-gray-100 text-[var(--text-primary,#1A1A2E)] rounded-tl-sm'
      }`}>
        <div className="text-sm whitespace-pre-wrap leading-relaxed">{inhalt}</div>
      </div>
    </div>
  );
}

export default function SalesChat() {
  const { token } = useParams();
  const [nachrichten, setNachrichten] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [leadInfo, setLeadInfo] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [nachrichten, sending]);

  // Chat laden
  useEffect(() => {
    async function loadChat() {
      try {
        const res = await fetch(`${API}/api/sales/chat/${token}`);
        const data = await res.json();
        if (data.error) {
          setError(data.error);
          return;
        }
        setLeadInfo(data.lead);
        if (data.nachrichten && data.nachrichten.length > 0) {
          setNachrichten(data.nachrichten);
        } else {
          // Einstiegs-Nachricht
          const name = data.lead?.ansprechpartner || 'du';
          setNachrichten([{
            rolle: 'assistant',
            inhalt: `Hallo ${name}! 👋\n\nIch bin der WeShip Assistent. Du hast Interesse an unserer Fulfillment-Lösung — das freut mich! Ich bin hier um alle deine Fragen zu beantworten.\n\nWas möchtest du wissen? Z.B.:\n• Wie funktioniert das Pricing?\n• Welche Shop-Systeme unterstützt ihr?\n• Wie läuft das Onboarding ab?`,
          }]);
        }
      } catch {
        setError('Chat konnte nicht geladen werden');
      } finally {
        setLoading(false);
      }
    }
    loadChat();
  }, [token]);

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setInput('');
    setNachrichten(prev => [...prev, { rolle: 'user', inhalt: text }]);
    setSending(true);

    try {
      const res = await fetch(`${API}/api/sales/chat/${token}/nachricht`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nachricht: text }),
      });
      const data = await res.json();
      if (data.antwort) {
        setNachrichten(prev => [...prev, { rolle: 'assistant', inhalt: data.antwort }]);
      }
    } catch {
      setNachrichten(prev => [...prev, {
        rolle: 'assistant',
        inhalt: 'Entschuldigung, es gab einen Fehler. Bitte versuche es erneut.',
      }]);
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
            <span className="text-white font-extrabold text-lg">W</span>
          </div>
          <div className="animate-spin h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center mx-auto mb-4">
            <span className="text-white font-extrabold text-lg">W</span>
          </div>
          <p className="text-[var(--text-secondary,#4B5563)]">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header */}
      <div className="bg-white border-b border-[var(--border-default,#E5E7EB)] px-4 py-3">
        <div className="max-w-[680px] mx-auto flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
            <span className="text-white font-extrabold text-sm">W</span>
          </div>
          <div>
            <div className="font-semibold text-[var(--text-primary,#1A1A2E)] text-sm">WeShip Assistent</div>
            <div className="text-xs text-[var(--text-secondary,#4B5563)]">
              {leadInfo?.firma ? `Chat mit ${leadInfo.firma}` : 'Fulfillment-Beratung'}
            </div>
          </div>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-[680px] mx-auto px-4 py-6 space-y-4">
          {nachrichten.map((msg, i) => (
            <ChatBubble key={i} rolle={msg.rolle} inhalt={msg.inhalt} />
          ))}
          {sending && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      </div>

      {/* Input */}
      <div className="bg-white border-t border-[var(--border-default,#E5E7EB)] px-4 py-3">
        <div className="max-w-[680px] mx-auto flex gap-2">
          <textarea
            ref={inputRef}
            value={input}
            onChange={e => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Schreib eine Nachricht..."
            rows={1}
            className="flex-1 resize-none rounded-xl border border-[var(--border-default,#E5E7EB)] px-4 py-2.5 text-sm focus:outline-none focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            disabled={sending}
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || sending}
            className="px-4 py-2.5 bg-primary-600 text-white rounded-xl text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
            </svg>
          </button>
        </div>
        <div className="max-w-[680px] mx-auto mt-2">
          <p className="text-[11px] text-[var(--text-tertiary,#6B7280)] text-center">
            Powered by WeShip AI — Antworten sind automatisch generiert
          </p>
        </div>
      </div>
    </div>
  );
}
